import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Typography, Paper, Button } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { paginationSize } from '../../constants';

// Check if a value looks like a date
const isDateValue = (value) => {
  if (!value) return false;

  // Check if it's already a Date object
  if (value instanceof Date) return !isNaN(value);

  // Check if it's a string that looks like a date
  if (typeof value === 'string') {
    // ISO 8601 format (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss)
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?/;
    if (isoDateRegex.test(value)) {
      const date = new Date(value);
      return !isNaN(date.getTime());
    }
  }

  return false;
};

// Format a date value as a short date (MM/DD/YYYY)
const formatDateValue = (value) => {
  if (!value) return value;

  const date = new Date(value);
  if (isNaN(date.getTime())) return value;

  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
};

// Format a boolean value as Yes/No
const formatBooleanValue = (value) => {
  // Handle truthy boolean representations: 1, '1', true, 'true' (case-insensitive)
  if (value === 1 || value === '1' || value === true) return 'Yes';
  if (typeof value === 'string' && value.toLowerCase() === 'true') return 'Yes';

  // Handle explicit false representations: 0, '0', false, 'false' (case-insensitive)
  if (value === 0 || value === '0' || value === false) return 'No';
  if (typeof value === 'string' && value.toLowerCase() === 'false') return 'No';

  // Handle other falsy values (null, undefined, empty string, etc.)
  if (!value) return 'No';

  // Otherwise, return as-is (for any other unexpected values)
  return value;
};

const QueryResultsGrid = ({ results, selectedFields, onExportCSV }) => {
  if (!results || !results.data || results.data.length === 0) {
    return (
      <Paper sx={{ p: 3, mt: 2, textAlign: 'center' }}>
        <Typography variant='body1' color='text.secondary'>
          No results to display. Execute a query to see results here.
        </Typography>
      </Paper>
    );
  }

  // Generate columns from the first row of data
  const columns = Object.keys(results.data[0] || {}).map((key) => {
    // Check if this column contains date values by sampling the first row
    const firstValue = results.data[0][key];
    const isDateColumn = isDateValue(firstValue);

    // Check if this column is a boolean field by matching friendly name
    const isBooleanColumn = selectedFields?.some(
      (field) => field.fieldFriendlyName === key && field.dataType === 'boolean'
    );

    return {
      field: key,
      headerName: key,
      flex: 1,
      minWidth: 150,
      ...(isDateColumn && {
        valueFormatter: (params) => formatDateValue(params.value)
      }),
      ...(isBooleanColumn && {
        valueFormatter: (params) => formatBooleanValue(params.value)
      })
    };
  });

  // Add row IDs if not present
  const rows = results.data.map((row, index) => ({
    id: row.id || index,
    ...row,
  }));

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant='h6'>Query Results</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant='body2' color='text.secondary'>
            {results.totalRecords || rows.length} record(s) found
          </Typography>
          <Button
            variant='outlined'
            color='success'
            size='small'
            startIcon={<DownloadIcon />}
            onClick={onExportCSV}
          >
            Export CSV
          </Button>
        </Box>
      </Box>
      <Paper sx={{ height: 400, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={paginationSize}
          pagination
          disableSelectionOnClick
          experimentalFeatures={{ newEditingApi: true }}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          }
          sx={{
            '& .MuiDataGrid-cell': {
              py: 1,
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default QueryResultsGrid;
